import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Share2, FileText, Download, Shield, Calculator } from "lucide-react";
import { formatCurrency, formatNumber, formatPercentage } from "@/utils/formatters";
import ExportControls from "@/components/ExportControls";

interface PublicShareData {
  success: boolean;
  data: any;
  module: string;
  isPublicView: boolean;
}

export function PublicShareScreen() {
  const { module, token } = useParams();
  const [location, setLocation] = useLocation();

  // VALUE ASSESSMENT ONLY platform - no maturity redirects needed

  const { data: shareData, isLoading, error } = useQuery<PublicShareData>({
    queryKey: ['/api/share', module, token],
    queryFn: async () => {
      const response = await fetch(`/api/share/${module}/${token}`);
      if (!response.ok) {
        throw new Error('Failed to load shared assessment');
      }
      return response.json();
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#4AE2C9] mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading shared assessment...</p>
        </div>
      </div>
    );
  }

  if (error || !shareData?.success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple to-slate-900 flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2 text-red-600">
              <Shield className="w-6 h-6" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600">
              This shared link is invalid or has expired.
            </p>
            <Button 
              onClick={() => window.location.href = '/'}
              className="w-full"
            >
              Return to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const resultData = shareData.data;
  const moduleType = shareData.module;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple to-slate-900">
      {/* Header */}
      <div className="bg-[#0D1224] border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#4AE2C9] rounded flex items-center justify-center">
                <Share2 className="w-4 h-4 text-black" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">
                  Shared Assessment Results
                </h1>
                <p className="text-sm text-gray-400">
                  Read-only view • {moduleType.charAt(0).toUpperCase() + moduleType.slice(1)} Assessment
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ExportControls
                resultData={resultData}
                moduleType={moduleType}
                isPublicView={true}
                showImageExport={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-export-content>
        {/* VALUE ASSESSMENT ONLY platform */}
        {moduleType === 'value' && <ValuePublicView data={resultData} />}
      </div>
    </div>
  );
}

// MaturityPublicView removed - VALUE ASSESSMENT ONLY platform

function ROIPublicView({ data }: { data: any }) {
  const roiData = data.data || data;
  
  // Parse the data structure based on the JSON shown in the screenshot
  const totalValue = roiData.totalValue || 0;
  const annualSavings = roiData.annualSavings || 0;
  const implementationCost = roiData.implementationCost || 0;
  const annualSubscriptionCost = roiData.annualSubscriptionCost || 0;
  const breakEvenPoint = roiData.breakEvenPoint || {};
  const pillarSavingsBreakdown = roiData.pillarSavingsBreakdown || {};
  const financialTable = roiData.financialTable || [];
  
  // Calculate key metrics
  const totalInvestment = implementationCost + annualSubscriptionCost;
  const roiPercentage = totalInvestment > 0 ? ((totalValue - totalInvestment) / totalInvestment * 100).toFixed(1) : 0;
  
  return (
    <div className="space-y-6">
      <Card className="bg-white/5 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <FileText className="w-5 h-5" />
            ROI Analysis Results
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Key Metrics */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="text-center p-6 bg-gradient-to-br from-blue-600/20 to-blue-800/20 border border-blue-500/30 rounded-lg">
              <div className="text-3xl font-bold text-[#4AE2C9] mb-2">
                {formatCurrency(totalInvestment)}
              </div>
              <p className="text-gray-300 font-medium">Total Investment</p>
            </div>
            
            <div className="text-center p-6 bg-gradient-to-br from-green-600/20 to-green-800/20 border border-green-500/30 rounded-lg">
              <div className="text-3xl font-bold text-green-400 mb-2">
                {formatCurrency(annualSavings)}
              </div>
              <p className="text-gray-300 font-medium">Annual Savings</p>
            </div>
          </div>

          {/* Detailed Calculation Breakdown */}
          <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/40 border border-slate-700/50 rounded-xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Calculator className="w-5 h-5 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white">Calculation Methodology</h3>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Cost Breakdown */}
              <div className="space-y-6">
                <h4 className="text-lg font-semibold text-red-300 border-b border-red-500/30 pb-2">
                  Total Cost Calculation
                </h4>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-slate-700/30 rounded-lg">
                    <span className="text-gray-300">Implementation Cost</span>
                    <span className="text-white font-semibold">
                      {formatCurrency(implementationCost)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-slate-700/30 rounded-lg">
                    <span className="text-gray-300">Annual Subscription Cost</span>
                    <span className="text-white font-semibold">
                      {formatCurrency(annualSubscriptionCost)}
                    </span>
                  </div>
                  
                  <div className="border-t border-gray-600 pt-3">
                    <div className="flex justify-between items-center p-4 bg-red-800/30 rounded-lg border border-red-400/50">
                      <span className="text-red-100 font-bold text-lg">Total Investment</span>
                      <span className="text-red-50 font-bold text-xl">
                        {formatCurrency(totalInvestment)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Benefits Breakdown */}
              <div className="space-y-6">
                <h4 className="text-lg font-semibold text-emerald-300 border-b border-emerald-500/30 pb-2">
                  Total Benefits Calculation
                </h4>
                
                <div className="space-y-4">
                  {Object.keys(pillarSavingsBreakdown).length > 0 && (
                    <div className="space-y-3">
                      {Object.entries(pillarSavingsBreakdown).map(([pillar, data]: [string, any]) => (
                        <div key={pillar} className="p-4 bg-emerald-900/20 rounded-lg border border-emerald-500/30">
                          <h5 className="font-semibold text-emerald-200 mb-3 capitalize">
                            {pillar.replace(/[_-]/g, ' ')}
                          </h5>
                          <div className="space-y-2 text-sm">
                            {data.Journals && (
                              <div className="flex justify-between">
                                <span className="text-gray-300">Journal Processing:</span>
                                <span className="text-emerald-200">{formatCurrency(data.Journals)}</span>
                              </div>
                            )}
                            {data.Reconciliations && (
                              <div className="flex justify-between">
                                <span className="text-gray-300">Reconciliations:</span>
                                <span className="text-emerald-200">{formatCurrency(data.Reconciliations)}</span>
                              </div>
                            )}
                            {data.Reporting && (
                              <div className="flex justify-between">
                                <span className="text-gray-300">Reporting:</span>
                                <span className="text-emerald-200">{formatCurrency(data.Reporting)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="border-t border-gray-600 pt-3">
                    <div className="flex justify-between items-center p-3 bg-emerald-900/20 rounded-lg border border-emerald-500/30">
                      <span className="text-emerald-200 font-semibold">Annual Savings</span>
                      <span className="text-emerald-100 font-bold text-lg">
                        {formatCurrency(annualSavings)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center p-4 bg-emerald-800/30 rounded-lg border border-emerald-400/50 mt-3">
                      <span className="text-emerald-100 font-bold text-lg">5-Year Total Value</span>
                      <span className="text-emerald-50 font-bold text-xl">
                        {formatCurrency(totalValue)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Final ROI Calculation */}
            <div className="mt-8 p-6 bg-gradient-to-r from-blue-900/30 to-purple/30 rounded-xl border border-blue-500/30">
              <h4 className="text-lg font-semibold text-blue-300 mb-4">ROI Calculation Formula</h4>
              <div className="space-y-3 text-sm text-gray-300">
                <div className="flex items-center justify-between">
                  <span>5-Year Net Value = Total Benefits - Total Investment</span>
                  <span className="text-white font-semibold">
                    {formatCurrency(totalValue - totalInvestment)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>ROI % = (Net Value ÷ Total Investment) × 100</span>
                  <span className="text-white font-semibold">
                    {roiPercentage}%
                  </span>
                </div>
                {breakEvenPoint.months && (
                  <div className="flex items-center justify-between">
                    <span>Payback Period = Total Investment ÷ Monthly Savings</span>
                    <span className="text-white font-semibold">
                      {Math.round(breakEvenPoint.months)} months
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ROI Metrics */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-800 rounded-lg">
              <div className="text-xl font-bold text-yellow-400 mb-1">
                {roiPercentage}%
              </div>
              <p className="text-sm text-gray-400">5-Year ROI</p>
            </div>
            
            {breakEvenPoint.years && (
              <div className="text-center p-4 bg-gray-800 rounded-lg">
                <div className="text-xl font-bold text-orange-400 mb-1">
                  {breakEvenPoint.years}
                </div>
                <p className="text-sm text-gray-400">Break-even (Years)</p>
              </div>
            )}
            
            {breakEvenPoint.months && (
              <div className="text-center p-4 bg-gray-800 rounded-lg">
                <div className="text-xl font-bold text-purple mb-1">
                  {Math.round(breakEvenPoint.months)}
                </div>
                <p className="text-sm text-gray-400">Break-even (Months)</p>
              </div>
            )}
          </div>

          {/* Pillar Savings Breakdown */}
          {Object.keys(pillarSavingsBreakdown).length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Savings by Pillar</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {Object.entries(pillarSavingsBreakdown).map(([pillar, data]: [string, any]) => (
                  <div key={pillar} className="p-4 bg-gray-800 rounded-lg">
                    <h4 className="font-semibold text-white mb-2 capitalize">
                      {pillar.replace(/[_-]/g, ' ')}
                    </h4>
                    <div className="space-y-2 text-sm">
                      {data.Journals && (
                        <div className="text-green-400">
                          Journals: {formatCurrency(data.Journals)}
                        </div>
                      )}
                      {data.Intercompany && (
                        <div className="text-blue-400">
                          Intercompany: {formatCurrency(data.Intercompany)}
                        </div>
                      )}
                      {data.Orchestration && (
                        <div className="text-purple">
                          Orchestration: {formatCurrency(data.Orchestration)}
                        </div>
                      )}
                      {data['Close management'] && (
                        <div className="text-orange-400">
                          Close Management: {formatCurrency(data['Close management'])}
                        </div>
                      )}
                      {data['Account reconciliation'] && (
                        <div className="text-teal-400">
                          Account Reconciliation: {formatCurrency(data['Account reconciliation'])}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Financial Table */}
          {financialTable.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">5-Year Financial Projection</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left p-2 text-gray-300">Year</th>
                      <th className="text-right p-2 text-gray-300">Setup Cost</th>
                      <th className="text-right p-2 text-gray-300">Subscription</th>
                      <th className="text-right p-2 text-gray-300">Savings</th>
                      <th className="text-right p-2 text-gray-300">Net Benefit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {financialTable.map((row: any, index: number) => (
                      <tr key={index} className="border-b border-gray-800">
                        <td className="p-2 text-white">{row.year}</td>
                        <td className="p-2 text-right text-red-400">
                          {formatCurrency(row.setupCost || 0)}
                        </td>
                        <td className="p-2 text-right text-orange-400">
                          {formatCurrency(row.subscriptionCost || 0)}
                        </td>
                        <td className="p-2 text-right text-green-400">
                          {formatCurrency(row.savings || 0)}
                        </td>
                        <td className="p-2 text-right text-[#4AE2C9] font-semibold">
                          {formatCurrency(row.netBenefit || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ValuePublicView({ data }: { data: any }) {
  const valueData = data.roi_summary || {};
  
  return (
    <div className="space-y-6">
      <Card className="bg-white/5 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Value Engineering Results
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Summary Metrics */}
          {valueData.summary && (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(valueData.summary).map(([key, value]: [string, any]) => (
                <div key={key} className="text-center p-4 bg-gray-800 rounded-lg">
                  <div className="text-xl font-bold text-[#4AE2C9] mb-1">
                    {typeof value === 'number' ? formatCurrency(value) : value}
                  </div>
                  <p className="text-sm text-gray-400 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Full Data */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Complete Analysis</h3>
            <pre className="text-gray-300 text-sm whitespace-pre-wrap overflow-auto">
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// BenchmarkingPublicView removed - VALUE ASSESSMENT ONLY platform